import { InfoWindow } from '@vis.gl/react-google-maps'
import { Phone, Search, X } from 'lucide-react'
import { Badge, Button } from '@/components/ds2'
import type { BoxTruckMapDriver } from '../services/boxTruckLoadsApi'
import { fmtProfileLocation } from '../utils/format'
import { BoxTruckLocationAgeBadge } from './BoxTruckLocationAgeBadge'

interface BoxTruckDriverDetailsProps {
    driver: BoxTruckMapDriver
    onClose: () => void
    onFindLoads: (driver: BoxTruckMapDriver) => void
}

export const BoxTruckDriverDetails = ({ driver, onClose, onFindLoads }: BoxTruckDriverDetailsProps) => (
    <InfoWindow
        position={{ lat: driver.lat, lng: driver.lng }}
        onCloseClick={onClose}
        pixelOffset={[0, -42]}
        headerDisabled
        minWidth={248}
        className="box-truck-driver-info-window bg-surface text-foreground"
    >
        <div className="relative min-w-60 bg-surface p-3 font-sans text-foreground">
            <button
                type="button"
                onClick={onClose}
                aria-label="Close driver details"
                className="absolute right-1.5 top-1.5 rounded-lg p-1 text-foreground-muted transition-colors hover:bg-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                <X size={16} aria-hidden />
            </button>
            <div className="flex items-center gap-1.5 pr-6">
                <p className="text-sm font-semibold">{driver.name || 'Box Truck driver'}</p>
                {!driver.verified && <Badge size="sm" variant="soft" type="warning" label="Unverified" />}
            </div>
            <p className="mt-0.5 text-xs font-medium text-foreground">
                {driver.equipment || 'Equipment not provided'}
                {driver.truckNumber ? ` · Truck #${driver.truckNumber.replace(/^#/, '')}` : ''}
            </p>
            <p className="mt-2 text-xs text-foreground">
                {fmtProfileLocation(driver.city, driver.state) || `${driver.lat.toFixed(5)}, ${driver.lng.toFixed(5)}`}
            </p>
            {/* The map no longer hides positions older than a day, so age is the operator's own judgement
                call — and a bare timestamp does not make it. Same badge the Nearby Drivers table uses:
                green under 30 min, amber under 6 h, red beyond, with the exact time on hover. */}
            <div className="mt-1 flex items-center gap-1.5 text-xs text-foreground-muted">
                <span>Last seen</span>
                <BoxTruckLocationAgeBadge fixAt={driver.lastSeenAt} />
            </div>
            {driver.phone && (
                <a href={`tel:${driver.phone}`} className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-accent hover:underline">
                    <Phone size={13} aria-hidden /> {driver.phone}
                </a>
            )}
            <Button size="sm" className="mt-3 w-full" onClick={() => onFindLoads(driver)} prefix={<Search size={14} aria-hidden />}>
                Find loads
            </Button>
        </div>
    </InfoWindow>
)
